// src/api/adminApi.js
import axiosInstance from './axiosConfig';

export const adminApi = {
  // ============================================================
  // DASHBOARD
  // ============================================================
  getDashboardStats: async () => {
    return axiosInstance.get('/admin/dashboard');
  },

  // ============================================================
  // USERS
  // ============================================================
  getUsers: async (params = {}) => {
    return axiosInstance.get('/admin/users', { params });
  },

  getUser: async (id) => {
    return axiosInstance.get(`/admin/users/${id}`);
  },

  updateUser: async (id, data) => {
    return axiosInstance.put(`/admin/users/${id}`, data);
  },

  deleteUser: async (id) => {
    return axiosInstance.delete(`/admin/users/${id}`);
  },

  createUser: async (data) => {
    return axiosInstance.post('/admin/users', data);
  },

  // ============================================================
  // VENDORS
  // ============================================================
  getVendors: async (params = {}) => {
    return axiosInstance.get('/admin/vendors', { params });
  },

  getVendor: async (id) => {
    return axiosInstance.get(`/admin/vendors/${id}`);
  },

  verifyVendor: async (id) => {
    return axiosInstance.post(`/admin/vendors/${id}/verify`);
  },

  suspendVendor: async (id) => {
    return axiosInstance.post(`/admin/vendors/${id}/suspend`);
  },

  updateVendorRating: async (id, rating) => {
    return axiosInstance.put(`/admin/vendors/${id}/rating`, { rating });
  },

  updateVendor: async (id, data) => {
    return axiosInstance.put(`/admin/vendors/${id}`, data);
  },

  getVendorFinance: async (vendorId) => {
    return axiosInstance.get(`/admin/vendors/${vendorId}/finance`);
  },

  // ============================================================
  // ORDERS
  // ============================================================
  getOrders: async (params = {}) => {
    return axiosInstance.get('/admin/orders', { params });
  },

  updateOrderStatus: async (id, data) => {
    return axiosInstance.put(`/admin/orders/${id}/status`, data);
  },

  getOrder: async (id) => {
    return axiosInstance.get(`/admin/orders/${id}`);
  },

  // ============================================================
  // PAYOUTS
  // ============================================================
  getPayouts: async (params = {}) => {
    return axiosInstance.get('/admin/payouts', { params });
  },

  updatePayoutStatus: async (id, status) => {
    return axiosInstance.put(`/admin/payouts/${id}`, { status });
  },

  // ============================================================
  // KYC VERIFICATION
  // ============================================================
  getKYCRequests: async (params = {}) => {
    return axiosInstance.get('/admin/kyc-requests', { params });
  },

  approveKYC: async (vendorId) => {
    return axiosInstance.post(`/admin/kyc/${vendorId}/approve`);
  },

  rejectKYC: async (vendorId) => {
    return axiosInstance.post(`/admin/kyc/${vendorId}/reject`);
  },

  // ============================================================
  // COUPONS
  // ============================================================
  getAllCoupons: async (params = {}) => {
    return axiosInstance.get('/coupons/admin/all', { params });
  },

  adminToggleCoupon: async (id, isActive) => {
    return axiosInstance.put(`/coupons/${id}/toggle`, { isActive });
  },

  // ============================================================
  // CATEGORIES
  // ============================================================
  getCategories: async (showAll = true) => {
    const response = await axiosInstance.get('/options/admin/categories', { 
      params: { showAll: showAll ? 'true' : 'false' } 
    });
    return response.data;
  },

  createCategory: async (data) => {
    const response = await axiosInstance.post('/options/admin/categories', data);
    return response.data;
  },

  updateCategory: async (id, data) => {
    const response = await axiosInstance.put(`/options/admin/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await axiosInstance.delete(`/options/admin/categories/${id}`);
    return response.data;
  },

  // ============================================================
  // SIZES
  // ============================================================
  getSizes: async (showAll = true) => {
    const response = await axiosInstance.get('/options/admin/sizes', { 
      params: { showAll: showAll ? 'true' : 'false' } 
    });
    return response.data;
  },

  createSize: async (data) => {
    const response = await axiosInstance.post('/options/admin/sizes', data);
    return response.data;
  },

  updateSize: async (id, data) => {
    const response = await axiosInstance.put(`/options/admin/sizes/${id}`, data);
    return response.data;
  },

  toggleSize: async (id, isActive) => {
    const response = await axiosInstance.put(`/options/admin/sizes/${id}`, { isActive });
    return response.data;
  },

  deleteSize: async (id) => {
    const response = await axiosInstance.delete(`/options/admin/sizes/${id}`);
    return response.data;
  },

  // ============================================================
  // COLORS
  // ============================================================
  getColors: async (showAll = true) => {
    const response = await axiosInstance.get('/options/admin/colors', { 
      params: { showAll: showAll ? 'true' : 'false' } 
    });
    return response.data;
  },

  createColor: async (data) => {
    const response = await axiosInstance.post('/options/admin/colors', data);
    return response.data;
  },

  updateColor: async (id, data) => {
    const response = await axiosInstance.put(`/options/admin/colors/${id}`, data);
    return response.data;
  },

  toggleColor: async (id, isActive) => {
    const response = await axiosInstance.put(`/options/admin/colors/${id}`, { isActive });
    return response.data;
  },

  deleteColor: async (id) => {
    const response = await axiosInstance.delete(`/options/admin/colors/${id}`);
    return response.data;
  },

  // ============================================================
  // BADGES
  // ============================================================
  getBadges: async (showAll = true) => {
    const response = await axiosInstance.get('/options/admin/badges', { 
      params: { showAll: showAll ? 'true' : 'false' } 
    });
    return response.data;
  },

  createBadge: async (data) => {
    const response = await axiosInstance.post('/options/admin/badges', data);
    return response.data;
  },

  updateBadge: async (id, data) => {
    const response = await axiosInstance.put(`/options/admin/badges/${id}`, data);
    return response.data;
  },

  toggleBadge: async (id, isActive) => {
    const response = await axiosInstance.put(`/options/admin/badges/${id}`, { isActive });
    return response.data;
  },

  deleteBadge: async (id) => {
    const response = await axiosInstance.delete(`/options/admin/badges/${id}`);
    return response.data;
  },

  // ============================================================
  // CHAT / MESSAGES (Admin)
  // ============================================================
  getAdminMessages: async () => {
    return axiosInstance.get('/chat/admin/rooms');
  },

  // ============================================================
  // PRODUCTS (Admin)
  // ============================================================
  getAdminProducts: async (params = {}) => {
    return axiosInstance.get('/admin/products', { params });
  },

  getAdminProductStats: async () => {
    return axiosInstance.get('/admin/products/stats');
  },

  createProduct: async (data) => {
    return axiosInstance.post('/products', data);
  },

  updateProduct: async (id, data) => {
    return axiosInstance.put(`/products/${id}`, data);
  },

  deleteProduct: async (id) => {
    return axiosInstance.delete(`/products/${id}`);
  },

  // ============================================================
  // SUPPORT TICKETS (Admin)
  // ============================================================
  getSupportTickets: async (params = {}) => {
    return axiosInstance.get('/support/admin/tickets', { params });
  },

  getSupportTicket: async (id) => {
    return axiosInstance.get(`/support/admin/tickets/${id}`);
  },

  replySupportTicket: async (id, data) => {
    return axiosInstance.post(`/support/admin/tickets/${id}/reply`, data);
  },

  updateTicketStatus: async (id, status) => {
    return axiosInstance.put(`/support/admin/tickets/${id}/status`, { status });
  },

  deleteSupportTicket: async (id) => {
    return axiosInstance.delete(`/support/admin/tickets/${id}`);
  },

  getSupportStats: async () => {
    return axiosInstance.get('/support/admin/stats');
  },

  // ============================================================
  // FAQ MANAGEMENT (Admin)
  // ============================================================
  getFaqs: async (params = {}) => {
    return axiosInstance.get('/support/admin/faq', { params });
  },

  createFaq: async (data) => {
    return axiosInstance.post('/support/admin/faq', data);
  },

  updateFaq: async (id, data) => {
    return axiosInstance.put(`/support/admin/faq/${id}`, data);
  },

  deleteFaq: async (id) => {
    return axiosInstance.delete(`/support/admin/faq/${id}`);
  },

  // ============================================================
  // BULK ACTIONS
  // ============================================================
  bulkUpdateTicketStatus: async (ticketIds, status) => {
    return axiosInstance.post('/support/admin/tickets/bulk', { ticketIds, status });
  },

  bulkDeleteTickets: async (ticketIds) => {
    return axiosInstance.post('/support/admin/tickets/bulk-delete', { ticketIds });
  },

  // ============================================================
  // ✅ NEW: VENDOR COMMISSION MANAGEMENT
  // ============================================================
  updateVendorCommission: async (data) => {
    return axiosInstance.put('/admin/settings/vendor-commission', data);
  },

  // ============================================================
  // ✅ NEW: SIINQEE BANK SETTINGS
  // ============================================================
  getSiinqeeSettings: async () => {
    return axiosInstance.get('/admin/settings/siinqee');
  },

  updateSiinqeeSettings: async (data) => {
    return axiosInstance.put('/admin/settings/siinqee', data);
  },

  // ============================================================
  // ✅ NEW: SHIPPING LOGISTICS SETTINGS
  // ============================================================
  getLogisticsSettings: async () => {
    return axiosInstance.get('/admin/settings/logistics');
  },

  updateLogisticsSettings: async (data) => {
    return axiosInstance.put('/admin/settings/logistics', data);
  },
   // ============================================================
  // ✅ LOGISTICS MANAGEMENT (Admin)
  // ============================================================
  getLogisticsPricing: async (params = {}) => {
    const response = await axiosInstance.get('/logistics/pricing', { params });
    return response.data;
  },

  createLogisticsPricing: async (data) => {
    const response = await axiosInstance.post('/logistics/pricing', data);
    return response.data;
  },

  updateLogisticsPricing: async (id, data) => {
    const response = await axiosInstance.put(`/logistics/pricing/${id}`, data);
    return response.data;
  },

  deleteLogisticsPricing: async (id) => {
    const response = await axiosInstance.delete(`/logistics/pricing/${id}`);
    return response.data;
  },

  toggleLogisticsPricing: async (id) => {
    const response = await axiosInstance.patch(`/logistics/pricing/${id}/toggle`);
    return response.data;
  },

// ============================================================
// ✅ CHAPA PAYMENT SETTINGS (Admin)
// ============================================================
getChapaSettings: async () => {
  const response = await axiosInstance.get('/settings/admin/payment');
  return response.data;
},

updateChapaSettings: async (data) => {
  const response = await axiosInstance.put('/settings/admin/payment', data);
  return response.data;
},
};